import React, { useState, useEffect, useCallback } from 'react';
import {
  usePlaidLink,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnExit,
  type PlaidLinkOnEvent,
} from 'react-plaid-link';
import Button from '../ui/Button';
import Card from '../ui/Card';
import LoadingSpinner from '../ui/LoadingSpinner';
import { plaidApi } from '../../services/plaid-api';
import toast from 'react-hot-toast';

interface PlaidLinkSetupProps {
  /** Business to file the new source under; backend validates it. */
  organizationId?: string | null;
  onSuccess: (dataSourceId: string) => void;
  onCancel: () => void;
}

export const PlaidLinkSetup: React.FC<PlaidLinkSetupProps> = ({ organizationId, onSuccess, onCancel }) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch Link token on component mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setIsLoading(true);
        const response = await plaidApi.createLinkToken();
        setLinkToken(response.linkToken);
      } catch (err) {
        console.error('Failed to create link token:', err);
        setError('Failed to initialize Plaid Link. Please try again.');
        toast.error('Failed to initialize Plaid Link');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLinkToken();
  }, []);

  // Handle successful Plaid Link flow
  const onPlaidSuccess = useCallback<PlaidLinkOnSuccess>(
    async (public_token, metadata) => {
      try {
        setIsLoading(true);

        // Exchange public token for access token and create data source
        const response = await plaidApi.exchangePublicToken(public_token, metadata, organizationId);

        toast.success(`Successfully connected to ${metadata.institution?.name || 'your bank'}`);
        onSuccess(response.dataSourceId);
      } catch (err) {
        console.error('Failed to exchange public token:', err);
        toast.error('Failed to connect your bank account. Please try again.');
        setError('Failed to connect your bank account.');
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, organizationId]
  );

  const onPlaidExit: PlaidLinkOnExit = (err, _metadata) => {
    if (err) {
      console.error('Plaid Link error:', err);
      toast.error('Connection cancelled or failed');
    }
  };

  const onPlaidEvent: PlaidLinkOnEvent = (eventName, metadata) => {
    console.log('Plaid Link event:', eventName, metadata);
  };

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
    onEvent: onPlaidEvent,
  };

  const { open, ready } = usePlaidLink(config);

  // Auto-open Plaid Link when ready
  useEffect(() => {
    if (ready && linkToken && !isLoading) {
      open();
    }
  }, [ready, linkToken, isLoading, open]);

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-muted-foreground">Initializing secure bank connection...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8">
        <div className="text-center space-y-4">
          <div className="text-destructive mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">Connection Error</p>
          </div>
          <p className="text-muted-foreground">{error}</p>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => window.location.reload()} variant="primary">
              Try Again
            </Button>
            <Button onClick={onCancel} variant="secondary">
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8">
      <div className="text-center space-y-4">
        <div className="mb-6">
          <svg className="w-16 h-16 mx-auto text-info mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h3 className="text-xl font-semibold text-foreground">Connect Your Bank Account</h3>
          <p className="text-muted-foreground mt-2">
            Securely connect your bank account using Plaid's encrypted connection
          </p>
        </div>

        <div className="bg-info/15 p-4 rounded-lg text-sm text-info">
          <p className="font-medium mb-1">Secure connection</p>
          <p>Your bank login credentials are never shared with Zamino — Plaid handles the connection with 256-bit encryption, and is trusted by thousands of apps.</p>
        </div>

        <div className="flex justify-center space-x-4 pt-4">
          <Button 
            onClick={() => open()} 
            disabled={!ready}
            variant="primary"
            size="lg"
          >
            Connect Bank Account
          </Button>
          <Button onClick={onCancel} variant="secondary" size="lg">
            Cancel
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          By connecting your account, you agree to share your financial data with Zamino for transaction analysis.
        </p>
      </div>
    </Card>
  );
}; 